import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import logoImg from 'src/public/img/mh-logo-text.svg';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    HeadingText20,
    HeadingText50,
    Input,
    InputType,
    pxToRem,
} from '@modernhealth/vital-components';
import { isEmailValid } from 'src/shared/helpers/emailValidator';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { color } from 'src/shared/styles/variables';
import { config } from 'src/config/config';

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

const CurrentEmail = styled.div({
    backgroundColor: color.gray05,
    padding: pxToRem(16),
    marginBottom: pxToRem(24),
    width: '100%',
});
const ButtonsContainer = styled.div({
    width: '100%',
    paddingTop: '40px',
    button: {
        '&:first-of-type': {
            flex: 1,
        },
        '&:last-of-type': {
            flex: 1,
        },
    },
});
const SingleButtonContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
});
const SingleButton = styled.div({
    width: '240px !important',
    display: 'flex',
});
const FormContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '504px',
    flexGrow: 1,
});
const InputContainer = styled.div({
    width: '100%',
    paddingTop: '28px',
});
const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '112px',
    justifyContent: 'center',
    flexGrow: 1,
});
export enum PreferredEmailPageTestId {
    EmailField = 'PreferredEmailPageTestId.EmailField',
    ContinueButton = 'PreferredEmailPageTestId.SubmitButton',
}
export const PreferredEmailPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [wasEmailEdited, setWasEmailEdited] = useState(false);
    const [showEmailTextBox, setShowEmailTextBox] = useState(false);
    const [isEligibilityEmail, setIsEligibilityEmail] = useState(false);
    const user = useSelector(getUserInformation);
    const { workEmailAddressChangeRequired } = useFlags();
    const isActiveCobraRifMember = user.isActiveCobraRifMember;
    const mandatoryWorkEmailAddressChange =
        workEmailAddressChangeRequired || isActiveCobraRifMember;
    const dispatch: Dispatch = useDispatch();
    const { t } = useTranslation();

    const onBlur = () => {
        setWasEmailEdited(true);
    };

    const onEmailChange = value => {
        setEmail(value);
        setIsEligibilityEmail(value === user.eligiblePersonEmail);
    };

    const handleSubmit = () => {
        if (showEmailTextBox || mandatoryWorkEmailAddressChange) {
            updateEmail();
        } else {
            continueWithoutUpdating();
        }
    };

    const updateEmail = async () => {
        try {
            await dispatch.user.updateEmailAddress(email);
            navigationService.navigate('/preferred-email-sent');
        } catch (err) {
            toastNotificationsService.renderErrorToast(err as string);
        }
    };

    const continueWithoutUpdating = useCallback(async () => {
        try {
            await dispatch.user.updateUserInfo({ emailConfirmed: true });
            navigationService.navigate('/completion-page');
        } catch (err) {
            toastNotificationsService.renderErrorToast(err as string);
        }
    }, [dispatch.user]);

    const defineEmailError = () => {
        if (isEligibilityEmail) {
            return t('auth:preferred_email.errors.work_email');
        } else if (wasEmailEdited && email && !isEmailValid(email)) {
            return t('translation:validation.invalid.email');
        } else {
            return '';
        }
    };

    const formValid = () => {
        if (showEmailTextBox || mandatoryWorkEmailAddressChange) {
            if (email === user.eligiblePersonEmail) {
                return false;
            } else {
                return isEmailValid(email);
            }
        } else {
            return true;
        }
    };

    useEffect(() => {
        if (user.ssoOnly) {
            continueWithoutUpdating();
        }
    }, [user.ssoOnly, continueWithoutUpdating]);

    // If a member is SSO-only we just plan on redirecting the member so no point in showing this screen
    if (user.ssoOnly) {
        return <CenteredSpinner />;
    }

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />
            <Container>
                <FormContainer>
                    <HeadingText50 marginBottom={24}>
                        {mandatoryWorkEmailAddressChange
                            ? t('auth:preferred_email.mandatory_title')
                            : t('auth:preferred_email.optional_title')}
                    </HeadingText50>
                    {!mandatoryWorkEmailAddressChange && (
                        <CurrentEmail>
                            <HeadingText20>{user.email}</HeadingText20>
                        </CurrentEmail>
                    )}
                    <BodyTextMedium>{t('auth:preferred_email.description')}</BodyTextMedium>
                    {(showEmailTextBox || mandatoryWorkEmailAddressChange) && (
                        <InputContainer>
                            <Input
                                onChange={onEmailChange}
                                onBlur={onBlur}
                                fieldId="email"
                                type={InputType.TextField}
                                testID={PreferredEmailPageTestId.EmailField}
                                value={email}
                                label={t('auth:preferred_email.email.label')}
                                error={defineEmailError()}
                            />
                        </InputContainer>
                    )}
                    <ButtonsContainer>
                        {/* This is a tech debt, we will tackle it in https://joinmodernhealth.atlassian.net/browse/FOUNDATION-305
                        we don't want to have the conditional logic, but to create a new component with the hide and show behavior*/}
                        {!mandatoryWorkEmailAddressChange ? (
                            <ButtonGroup>
                                <Button
                                    variant="secondary"
                                    size="large"
                                    onClick={() => setShowEmailTextBox(!showEmailTextBox)}
                                >
                                    {showEmailTextBox
                                        ? t('auth:preferred_email.button.cancel')
                                        : t('auth:preferred_email.button.change_email')}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="large"
                                    onClick={handleSubmit}
                                    disabled={!formValid()}
                                >
                                    {t('auth:preferred_email.button.continue')}
                                </Button>
                            </ButtonGroup>
                        ) : (
                            <SingleButtonContainer>
                                <SingleButton>
                                    <Button
                                        onClick={handleSubmit}
                                        variant="primary"
                                        disabled={!formValid()}
                                        data-testid={PreferredEmailPageTestId.ContinueButton}
                                        size="large"
                                        fluid
                                    >
                                        {t('auth:preferred_email.button.continue')}
                                    </Button>
                                </SingleButton>
                            </SingleButtonContainer>
                        )}
                    </ButtonsContainer>
                </FormContainer>
            </Container>
        </>
    );
};
